import { supabase } from '../lib/supabase';

export interface CandidateData {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  position: string;
  jobDescription: string;
  analysisScore?: number;
  lastAnalyzed?: Date;
  status: 'new' | 'interviewed' | 'hired' | 'rejected';
  skills?: string[];
  relevanceScore?: number;
  verdict?: string;
  technicalSkills?: Array<{
    skill: string;
    proficiency: string;
    evidence: string;
  }>;
  interviewQuestions?: {
    technical: string[];
    behavioral: string[];
    cultural: string[];
    riskFactors: string[];
    redFlags: string[];
    greenFlags: string[];
  };
  projectId?: string;
  analysisId?: string;
  interviewPerspectives?: Record<string, string>;
}

export interface CacheOptions {
  ttlMinutes?: number;
  forceRefresh?: boolean;
}

export interface CacheMetrics {
  hitRate: number;
  hitCount: number;
  missCount: number;
  totalSize: number;
  lastHit?: Date;
  lastMiss?: Date;
  cacheStatus: 'active' | 'expired';
}

export interface CacheEntry {
  candidates: CandidateData[];
  timestamp: number;
  userId: string;
  metadata?: Record<string, any>;
}

export class CandidateCacheService {
  private static instance: CandidateCacheService;
  private readonly defaultTtlMinutes = 15;

  private constructor() {}

  static getInstance(): CandidateCacheService {
    if (!CandidateCacheService.instance) {
      CandidateCacheService.instance = new CandidateCacheService();
    }
    return CandidateCacheService.instance;
  }

  /**
   * Get cached candidate data
   */
  async getCachedCandidates(
    userId: string,
    cacheKey: string,
    options: CacheOptions = {}
  ): Promise<CandidateData[] | null> {
    try {
      if (options.forceRefresh) {
        console.log(`🔄 Force refresh requested for cache key: ${cacheKey}`);
        return null;
      }

      const { data, error } = await supabase.rpc('get_candidate_cache', {
        p_user_id: userId,
        p_cache_key: cacheKey
      });

      if (error) {
        console.error('❌ Error getting cache:', error);
        return null;
      }

      if (!data) {
        console.log(`📭 Cache miss for key: ${cacheKey}`);
        return null;
      }

      const cacheEntry = data as CacheEntry;
      
      // Convert date strings back to Date objects
      const candidatesWithDates = cacheEntry.candidates.map(candidate => ({
        ...candidate,
        lastAnalyzed: candidate.lastAnalyzed ? new Date(candidate.lastAnalyzed) : undefined
      }));

      console.log(`🎯 Cache hit for key: ${cacheKey} (${candidatesWithDates.length} candidates)`);
      return candidatesWithDates;
    } catch (error) {
      console.error('❌ Error in getCachedCandidates:', error);
      return null;
    }
  }

  /**
   * Set cached candidate data
   */
  async setCachedCandidates(
    userId: string,
    cacheKey: string,
    candidates: CandidateData[],
    options: CacheOptions = {}
  ): Promise<boolean> {
    try {
      const ttlMinutes = options.ttlMinutes || this.defaultTtlMinutes;
      
      const cacheEntry: CacheEntry = {
        candidates,
        timestamp: Date.now(),
        userId,
        metadata: {
          candidateCount: candidates.length,
          cacheKey,
          ttlMinutes
        }
      };

      const { error } = await supabase.rpc('set_candidate_cache', {
        p_user_id: userId,
        p_cache_key: cacheKey,
        p_cache_data: cacheEntry,
        p_ttl_minutes: ttlMinutes
      });

      if (error) {
        console.error('❌ Error setting cache:', error);
        return false;
      }

      console.log(`💾 Cache set for key: ${cacheKey} (${candidates.length} candidates, TTL: ${ttlMinutes}min)`);
      return true;
    } catch (error) {
      console.error('❌ Error in setCachedCandidates:', error);
      return false;
    }
  }

  /**
   * Clear specific cache entry
   */
  async clearCache(userId: string, cacheKey: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('candidate_cache')
        .delete()
        .eq('user_id', userId)
        .eq('cache_key', cacheKey);

      if (error) {
        console.error('❌ Error clearing cache:', error);
        return false;
      }

      console.log(`🗑️ Cache cleared for key: ${cacheKey}`);
      return true;
    } catch (error) {
      console.error('❌ Error in clearCache:', error);
      return false;
    }
  }

  /**
   * Clear all cache entries for a user
   */
  async clearAllCache(userId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('candidate_cache')
        .delete()
        .eq('user_id', userId);

      if (error) {
        console.error('❌ Error clearing all cache:', error);
        return false;
      }

      console.log(`🗑️ All cache cleared for user: ${userId}`);
      return true;
    } catch (error) {
      console.error('❌ Error in clearAllCache:', error);
      return false;
    }
  }

  /**
   * Invalidate cache entries matching a pattern
   */
  async invalidateByPattern(userId: string, pattern: string): Promise<number> {
    try {
      const { data, error } = await supabase.rpc('invalidate_candidate_cache_pattern', {
        p_user_id: userId,
        p_cache_pattern: pattern
      });

      if (error) {
        console.error('❌ Error invalidating cache pattern:', error);
        return 0;
      }

      const deletedCount = data as number;
      console.log(`🗑️ Invalidated ${deletedCount} cache entries matching pattern: ${pattern}`);
      return deletedCount;
    } catch (error) {
      console.error('❌ Error in invalidateByPattern:', error);
      return 0;
    }
  }

  /**
   * Get cache metrics for a specific cache key
   */
  async getCacheMetrics(userId: string, cacheKey: string): Promise<CacheMetrics | null> {
    try {
      const { data, error } = await supabase
        .from('candidate_cache_performance')
        .select('*')
        .eq('user_id', userId)
        .eq('cache_key', cacheKey)
        .single();

      if (error) {
        console.error('❌ Error getting cache metrics:', error);
        return null;
      }

      return {
        hitRate: data.hit_rate_percent,
        hitCount: data.hit_count,
        missCount: data.miss_count,
        totalSize: data.total_size_bytes,
        lastHit: data.last_hit_at ? new Date(data.last_hit_at) : undefined,
        lastMiss: data.last_miss_at ? new Date(data.last_miss_at) : undefined,
        cacheStatus: data.cache_status
      };
    } catch (error) {
      console.error('❌ Error in getCacheMetrics:', error);
      return null;
    }
  }

  /**
   * Get all cache metrics for a user
   */
  async getAllCacheMetrics(userId: string): Promise<CacheMetrics[]> {
    try {
      const { data, error } = await supabase
        .from('candidate_cache_performance')
        .select('*')
        .eq('user_id', userId)
        .order('hit_count', { ascending: false });

      if (error) {
        console.error('❌ Error getting all cache metrics:', error);
        return [];
      }

      return data.map(item => ({
        hitRate: item.hit_rate_percent,
        hitCount: item.hit_count,
        missCount: item.miss_count,
        totalSize: item.total_size_bytes,
        lastHit: item.last_hit_at ? new Date(item.last_hit_at) : undefined,
        lastMiss: item.last_miss_at ? new Date(item.last_miss_at) : undefined,
        cacheStatus: item.cache_status
      }));
    } catch (error) {
      console.error('❌ Error in getAllCacheMetrics:', error);
      return [];
    }
  }

  /**
   * Clean expired cache entries manually
   */
  async cleanExpiredCache(): Promise<number> {
    try {
      const { data, error } = await supabase.rpc('clean_expired_candidate_cache');

      if (error) {
        console.error('❌ Error cleaning expired cache:', error);
        return 0;
      }

      const deletedCount = data as number;
      if (deletedCount > 0) {
        console.log(`🧹 Cleaned ${deletedCount} expired cache entries`);
      }
      return deletedCount;
    } catch (error) {
      console.error('❌ Error in cleanExpiredCache:', error);
      return 0;
    }
  }

  /**
   * Check if cache entry exists and is valid
   */
  async isCacheValid(userId: string, cacheKey: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('candidate_cache')
        .select('expires_at')
        .eq('user_id', userId)
        .eq('cache_key', cacheKey)
        .single();

      if (error || !data) {
        return false;
      }

      const now = new Date();
      const expiresAt = new Date(data.expires_at);
      return expiresAt > now;
    } catch (error) {
      console.error('❌ Error in isCacheValid:', error);
      return false;
    }
  }

  /**
   * Get cache size for a user
   */
  async getCacheSize(userId: string): Promise<{ totalEntries: number; totalSizeKB: number }> {
    try {
      const { data, error } = await supabase
        .from('candidate_cache_stats')
        .select('total_size_bytes')
        .eq('user_id', userId);

      if (error) {
        console.error('❌ Error getting cache size:', error);
        return { totalEntries: 0, totalSizeKB: 0 };
      }

      const totalSizeBytes = data.reduce((sum, item) => sum + (item.total_size_bytes || 0), 0);
      const totalSizeKB = Math.round((totalSizeBytes / 1024) * 100) / 100;

      return {
        totalEntries: data.length,
        totalSizeKB
      };
    } catch (error) {
      console.error('❌ Error in getCacheSize:', error);
      return { totalEntries: 0, totalSizeKB: 0 };
    }
  }

  /**
   * Migrate localStorage cache data to Supabase
   */
  async migrateLegacyCache(userId: string): Promise<{
    migrated: number;
    errors: string[];
  }> {
    const migrated: string[] = [];
    const errors: string[] = [];

    try {
      // Get all localStorage keys that look like candidate cache
      const cacheKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.includes('candidates_cache')) {
          cacheKeys.push(key);
        }
      }

      console.log(`🔄 Found ${cacheKeys.length} legacy cache entries to migrate`);

      for (const key of cacheKeys) {
        try {
          const cached = localStorage.getItem(key);
          if (!cached) continue;

          const cacheData = JSON.parse(cached);
          
          // Validate cache data structure
          if (!cacheData.candidates || !cacheData.timestamp || !cacheData.userId) {
            errors.push(`Invalid cache structure for key: ${key}`);
            continue;
          }

          // Skip if not for this user
          if (cacheData.userId !== userId) {
            continue;
          }

          // Check if cache is not too old (skip if older than 1 hour)
          const cacheAge = Date.now() - cacheData.timestamp;
          if (cacheAge > 60 * 60 * 1000) {
            console.log(`⏰ Skipping expired cache: ${key}`);
            continue;
          }

          // Extract cache key from localStorage key
          const cacheKey = key.replace(`candidates_cache_${userId}`, 'candidates_cache')
            .replace(`_${userId}`, '')
            .replace(/^candidates_cache_/, '')
            || 'default';

          // Migrate to Supabase
          const success = await this.setCachedCandidates(
            userId,
            cacheKey,
            cacheData.candidates,
            { ttlMinutes: 15 }
          );

          if (success) {
            migrated.push(key);
            // Remove from localStorage after successful migration
            localStorage.removeItem(key);
            console.log(`✅ Migrated cache: ${key} -> ${cacheKey}`);
          } else {
            errors.push(`Failed to migrate cache: ${key}`);
          }
        } catch (error) {
          errors.push(`Error migrating ${key}: ${error}`);
        }
      }

      console.log(`✅ Migration complete: ${migrated.length} entries migrated, ${errors.length} errors`);
      return {
        migrated: migrated.length,
        errors
      };
    } catch (error) {
      console.error('❌ Error in migrateLegacyCache:', error);
      return {
        migrated: 0,
        errors: [`Migration failed: ${error}`]
      };
    }
  }
}

// Export singleton instance
export const candidateCacheService = CandidateCacheService.getInstance(); 